// src/components/PersonalInfo.js
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api";
import "./PersonalInfo.css";

function normalize(p) {
  return {
    firstName: p?.firstName ?? "",
    lastName: p?.lastName ?? "",
    age: p?.age ?? "",
    weight: p?.weight ?? "",
    height: p?.height ?? "",
    sex: p?.sex ?? "",
    goal: p?.goal ?? "",
    trainingPlace: p?.trainingPlace ?? "",
    trainingDays: Array.isArray(p?.trainingDays) ? p.trainingDays : [],
    language: p?.language ?? "en",
  };
}
const keyOf = (obj) => JSON.stringify(obj, Object.keys(obj).sort());

export default function PersonalInfo({ profile: profileProp, onSave }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(!profileProp);
  const [profile, setProfile] = useState(profileProp || null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (profileProp) return;
      try {
        const res = await api("/me/profile"); // GET
        if (mounted) setProfile(res?.profile || {});
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [profileProp]);

  const initial = useMemo(() => normalize(profile), [profile]);
  const [form, setForm] = useState(initial);
  useEffect(() => {
    if (!editing) setForm(initial);
  }, [initial, editing]);

  const dirty = editing && keyOf(form) !== keyOf(initial);
  const disabled = !editing || saving;

  async function persistProfile(nextProfile) {
    if (typeof onSave === "function") {
      await onSave(nextProfile);
      return true;
    }
    await api("/me/profile", { method: "PUT", body: { profile: nextProfile } });
    return true;
  }

  async function handleSave() {
    if (!dirty) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await persistProfile(form);
      setProfile(form);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setForm(initial);
    setEditing(false);
  }

  function upd(k) {
    return (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  }

  if (loading) {
    return (
      <div>
        <div className="personal-header">
          <h3>{t("profile.personalInfoTitle")}</h3>
        </div>
        <p className="muted">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className={`${editing ? "is-editing" : ""}`}>
      <div className="personal-header">
        {!editing ? (
          <button className="btn" onClick={() => setEditing(true)}>
            {t("profile.edit")}
          </button>
        ) : (
          <div className="actions">
            <button className="btn" onClick={handleCancel} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button className="btn primary" onClick={handleSave} disabled={saving || !dirty}>
              {saving ? t("profile.saving") : t("profile.saveChanges")}
            </button>
          </div>
        )}
      </div>

      <div className="info-grid">
        <div className="field">
          <label>{t("profile.fields.firstName")}</label>
          <div className={`input-bubble ${disabled ? "is-disabled" : ""}`}>
            <input
              value={form.firstName}
              onChange={upd("firstName")}
              disabled={disabled}
              placeholder={t("profile.placeholders.firstName")}
            />
          </div>
        </div>

        <div className="field">
          <label>{t("profile.fields.lastName")}</label>
          <div className={`input-bubble ${disabled ? "is-disabled" : ""}`}>
            <input
              value={form.lastName}
              onChange={upd("lastName")}
              disabled={disabled}
              placeholder={t("profile.placeholders.lastName")}
            />
          </div>
        </div>

        <div className="field small">
          <label>{t("profile.fields.age")}</label>
          <div className={`input-bubble ${disabled ? "is-disabled" : ""}`}>
            <input
              type="number"
              inputMode="numeric"
              value={form.age}
              onChange={upd("age")}
              disabled={disabled}
              placeholder={t("profile.placeholders.age")}
            />
          </div>
        </div>

        <div className="field small">
          <label>{t("profile.fields.sex")}</label>
          <div className={`input-bubble ${disabled ? "is-disabled" : ""}`}>
            <select value={form.sex} onChange={upd("sex")} disabled={disabled}>
              <option value="">{t("profile.sexOptions.none")}</option>
              <option value="male">{t("profile.sexOptions.male")}</option>
              <option value="female">{t("profile.sexOptions.female")}</option>
            </select>
          </div>
        </div>

        <div className="field small">
          <label>{t("profile.fields.weightKg")}</label>
          <div className={`input-bubble ${disabled ? "is-disabled" : ""}`}>
            <input
              type="number"
              inputMode="decimal"
              value={form.weight}
              onChange={upd("weight")}
              disabled={disabled}
              placeholder={t("profile.placeholders.weightKg")}
            />
          </div>
        </div>

        <div className="field small">
          <label>{t("profile.fields.heightCm")}</label>
          <div className={`input-bubble ${disabled ? "is-disabled" : ""}`}>
            <input
              type="number"
              inputMode="decimal"
              value={form.height}
              onChange={upd("height")}
              disabled={disabled}
              placeholder={t("profile.placeholders.heightCm")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}